import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation as useRouterLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft } from 'lucide-react';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Skeleton } from '../../ui/skeleton';
import { useBreadcrumb } from '../../../hooks/useBreadcrumb';
import { useGymNameSuggestions } from '../../../hooks/useGymNameSuggestions';
import { zonesService } from '../../../services/zones';
import { climbsService } from '../../../services/climbs';
import { competitionRoundsService } from '../../../services/competition-rounds';
import { useToast } from '../../ui/use-toast';
import type { Climb, Hold, RecentClimbPhoto, RouteLine } from '../../../types/climb';
import type { RecentClimbScan } from '../../../types/climb-scan';
import type { Zone } from '../../../types/zone';
import { ValidatedTitleInput } from '../../climb-create/ValidatedTitleInput';
import WheelPickerField from '../WheelPickerField';
import type { WheelPickerOption } from '../../ui/wheel-picker';
import { ClimbEditorFab } from './ClimbEditorFab';
import { ClimbEditorSplit } from './ClimbEditorSplit';
import { ClimbExtrasDialog } from './ClimbExtrasDialog';
import { ClimbPhotoSource, type ZonePhoto } from './ClimbPhotoSource';
import { CropChoiceDialog } from './CropChoiceDialog';
import { HoldTypeDialog } from './HoldTypeDialog';
import { NameSuggestionsDialog } from './NameSuggestionsDialog';
import { PhotoClimbSurface } from './PhotoClimbSurface';
import {
  climbRequirements,
  isDrawingAdvice,
  requirementAnchor,
  requirementField,
  scrollToFirstRequirement,
  shortRequirementKey,
  type ClimbRequirementField,
} from './requirements';
import { UnmarkedClimbDialog } from './UnmarkedClimbDialog';
import {
  WHOLE_PHOTO,
  cropHolds,
  cropImage,
  cropLine,
  isCrop,
  visibleRect,
  type CropRect,
} from './crop';
import { takePhotoFor } from './pending-photo';
import { usePhotoUpload } from './usePhotoUpload';
import { useRecentClimbPhotos } from './useRecentClimbPhotos';
import { useRecentClimbScans } from './useRecentClimbScans';
import { useEditorSplit } from './useEditorSplit';
import { useExpandablePhoto } from './useExpandablePhoto';
import { editorExpandedHeight } from './photo-height';
import { wallScanFrom } from './wall-scan';
import { useLiveScan, type LiveScanOutcome } from './useLiveScan';
import { NO_FILTER, passesFilter, sizeRange, type HoldFilter } from './hold-filter';
import { gradesFor, useClimbEditorLocation } from './useClimbEditorLocation';
import { useClimbEditorAccess } from './useClimbEditorAccess';
import { useClimbPhoto } from './useClimbPhoto';
import { useClimbEditorSource } from './useClimbEditorSource';
import { climbTitle, describeClimb } from '../../../utils/climb-title';
import { climbUrl } from '../../../utils/climb-url';
import {
  holdsFromDetections,
  isPlaceholderTaken,
  minScoreFloor,
  untakenPlaceholders,
  wallPlaceholders,
} from '../../../gym-admin/components/scans/detected-holds';
import { climbScansService } from '../../../services/climb-scans';
import { useClearSelectionOutside } from './useClearSelectionOutside';
import { usePhotoHolds } from '../photo-editor/usePhotoHolds';
import { usePhotoLine } from '../photo-editor/usePhotoLine';
import {
  assignPhotoHands,
  clearPhotoHands,
  orderPhotoHolds,
  startingPhotoHand,
} from '../photo-circuit';
import type { BoardHand } from '../../../types/device';
import type { ClimbEditorRoute } from './useClimbEditorRoute';

/** A photo with no scan behind it offers no placeholders — one array for all of them. */
const NO_HOLDS: Hold[] = [];

/**
 * A boulder or a route on a photo of the wall it is on.
 *
 * The same three blocks the board branch has, with the board swapped for a
 * picture the setter supplies: the bar of controls at the top, the photo under
 * it, and the fields that gate the save at the bottom. What the bar offers and
 * what is drawn follow from the type — a boulder is circled hold by hold, a
 * route is one line from the start to the anchor — and what is *required*
 * follows from the place: see `requirements.ts`.
 */
export function PhotoClimbEditor({ route }: { route: ClimbEditorRoute }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  // Where the setter came from, when they came from a competition round: the
  // round page sends them here to make a climb *for that round*, so creating it
  // and leaving it unattached would only be half the job. `useRouterLocation`
  // rather than `useLocation` because `location` in here is the place the climb
  // belongs to, not the URL.
  const routerLocation = useRouterLocation();
  const returnToRound = (routerLocation.state ?? null) as {
    returnToRound?: boolean;
    competitionSlug?: string;
    roundId?: string;
    requiresJudge?: boolean;
  } | null;
  const roundToJoin =
    returnToRound?.returnToRound && returnToRound.competitionSlug && returnToRound.roundId
      ? { slug: returnToRound.competitionSlug, id: returnToRound.roundId, requiresJudge: !!returnToRound.requiresJudge }
      : null;
  const { location, loading } = useClimbEditorLocation(route.place, route.locationSlug);

  const [name, setName] = useState('');
  const [gradeId, setGradeId] = useState('');
  const [holdColor, setHoldColor] = useState('');
  const [position, setPosition] = useState<string>('');
  const [holds, setHolds] = useState<Hold[]>([]);
  const [line, setLine] = useState<RouteLine | null>(null);
  const [zones, setZones] = useState<ZonePhoto[]>([]);
  // The sectors themselves, for the picker the extras dialog shows.
  const [allZones, setAllZones] = useState<Zone[]>([]);
  // Circuit mode: the circles stop being a set and become the order they are
  // climbed in, each wearing its number. Boulders only — a route is one line from
  // the ground to the anchor, and a line is already in order.
  const [circuit, setCircuitState] = useState(false);
  // Forced hands: that order also says which hand takes each hold, alternating
  // from the one the first move is made with.
  const [forcedHands, setForcedHandsState] = useState(false);
  const [startingHand, setStartingHand] = useState<BoardHand>('left');
  // The list of the circuit, over the photo. Shut until it is asked for: the
  // circuit is painted on the picture, and this is for reading it back.
  const [circuitPanelOpen, setCircuitPanelOpen] = useState(false);
  const [changingPhoto, setChangingPhoto] = useState(false);
  // A crag route may have no photo at all, and then the fields are all there is
  // to fill in. Said once, out loud, rather than assumed from an empty page.
  const [skippedPhoto, setSkippedPhoto] = useState(false);
  const [managingNames, setManagingNames] = useState(false);

  // What the page was opened on, when it was not opened empty: the climb being
  // corrected, and — where a scan's "save & insert" sent us — what that scan
  // knows about it. See useClimbEditorSource.
  const source = useClimbEditorSource(route);
  // What the reader may do here: write the climb, or only give it the photograph
  // it was put up without. See useClimbEditorAccess.
  const access = useClimbEditorAccess(route.locationSlug, source.climb);
  /**
   * Whether the save waits for a photograph.
   *
   * Indoors a picture is how a climb is found on a wall of forty others, so a new
   * one does not go up without one. Three ways past that, each of them somebody
   * the demand would make no sense to:
   *
   * * **the place** — on rock a line is often recorded off a topo, with no photo
   *   to hand (`route.photoRequired`);
   * * a gym's **admin**, entering a wall as a list and leaving the pictures to
   *   the climbers on it — a camera stands where each one's photo would be until
   *   somebody at the wall takes it (see `wantsPhoto`);
   * * whoever **corrects** a climb that is already up without one: they came to
   *   fix a grade, and are not the reason it has no picture.
   *
   * A contributor is the one person it is never waived for — supplying the
   * picture is the whole of what they are here to do.
   */
  const correctingPhotolessClimb = access.manages && !!source.climb && !source.climb.has_image;
  const photoRequired =
    access.contributing ||
    (route.photoRequired && !access.administers && !correctingPhotolessClimb);
  /**
   * Whether the name is asked for here, before the save.
   *
   * Where the climb is known by it, as always — and to a contributor, who is
   * naming a climb that has none: they are the first person to stand in front of
   * it since it went up, and there is no dialog of extras for them afterwards.
   */
  const asksName = route.nameRequired || (access.contributing && !source.climb?.name);
  // A scan's draft has no picture of its own yet: the scan's photo is the one
  // its analysis measured every box against, so it has to be this one too.
  const photo = useClimbPhoto(
    source.scan?.photoUrl ?? source.climb?.image_medium_url ?? source.climb?.image_url ?? null
  );
  // A picture already chosen on the climb's own page, where the reader saw it
  // missing: the editor opens on it instead of asking for it a second time. Taken
  // rather than read, so nothing is handed over twice — see pending-photo.
  const choosePhoto = photo.choose;
  useEffect(() => {
    if (!route.climbSlug) return;
    const handed = takePhotoFor(route.climbSlug);
    if (handed) void choosePhoto(handed);
  }, [route.climbSlug, choosePhoto]);

  const { upload } = usePhotoUpload();
  const { toast } = useToast();
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState<Climb | null>(null);
  // The part of the photo on screen, held while the setter answers whether to
  // cut the picture down to it.
  const [pendingCrop, setPendingCrop] = useState<CropRect | null>(null);

  // Only a gym keeps a queue of names; a crag names its routes after the rock.
  const nameSuggestions = useGymNameSuggestions(
    route.place === 'gym' ? route.locationSlug : undefined
  );

  /**
   * The scanned wall the photo is, when it was picked off a scan that found
   * holds on it — and the climb is a boulder: a route is one line drawn over
   * the wall, and is never given one (see `pickScan`).
   *
   * **The wall is a fact and a climb is a choice of it.** Every hold the scan
   * found is on the photo as a placeholder, and the boulder is made by taking
   * some of them; nothing is drawn where the scan found nothing. A photo with
   * no scan behind it — or a scanned wall nothing has been found on yet — is
   * drawn on with circles as ever.
   */
  const wallScan = photo.photo?.scan ?? null;
  // Every hold the wall offers, as placeholders: what the save maps the
  // climb's holds back onto, whatever the filter hides.
  const placeholders = useMemo(
    () =>
      wallScan && photo.photo
        ? wallPlaceholders(wallScan.detections, photo.photo.naturalWidth, photo.photo.naturalHeight)
        : NO_HOLDS,
    [wallScan, photo.photo]
  );

  /**
   * Which of the wall's holds are offered: those the filter leaves — the
   * confidence and the size a reader can move from the bar under the photo
   * (see `hold-filter.ts`). Hidden, never deleted, and a hold already taken
   * into the climb stays in it.
   */
  const [holdFilter, setHoldFilter] = useState<HoldFilter>(NO_FILTER);
  const shownPlaceholders = useMemo(
    () =>
      wallScan && holdFilter !== NO_FILTER
        ? placeholders.filter((_placeholder, index) => passesFilter(wallScan.detections[index], holdFilter))
        : placeholders,
    [wallScan, placeholders, holdFilter]
  );
  // The ones this climb has not taken, still there to be pressed. A hold taken
  // and then binned is back among them: it belongs to the wall, not the climb.
  const candidates = useMemo(
    () => untakenPlaceholders(shownPlaceholders, holds),
    [shownPlaceholders, holds]
  );

  // The two editors live here rather than in the surface: at save time the page
  // needs the one in use, because its zoom is what says which part of the photo
  // is on screen — and that is the crop.
  const holdEditor = usePhotoHolds({
    naturalWidth: photo.photo?.naturalWidth ?? 1,
    naturalHeight: photo.photo?.naturalHeight ?? 1,
    holds,
    onChange: setHolds,
    repeatPressCycles: 'size',
    // On a circuit every change settles the order the holds are climbed in, and
    // the circles wear their number as they are drawn.
    circuit,
    // On a scanned wall a press on a placeholder is what a press on bare wall
    // is anywhere else: the hold goes in, in the role armed on the bar, and is
    // picked up so the bar acts on it next. Bare wall is then only wall, and
    // nothing taken is dragged or sized off the hold it was found on.
    drawing: !wallScan,
    pinned: !!wallScan,
    candidates: wallScan ? candidates : undefined,
  });

  // **The marks belong to the wall they were made on.** A scan's holds are
  // placeholders on its own photograph and nowhere else, so moving onto a
  // scanned wall, off one, or from one to another starts the drawing again —
  // where moving between two ordinary photos keeps it, as it always has. Not
  // undoable into either: the undo stack goes with the drawing.
  //
  // The filter goes with the wall too: a new one opens on whatever confidence
  // the gym saved on it, and on every hold whatever its size.
  const wallScanId = wallScan?.id ?? null;
  const drawnOnScan = useRef(wallScanId);
  const resetHoldEditor = useRef(holdEditor.reset);
  resetHoldEditor.current = holdEditor.reset;
  const wallMinScore = useRef(wallScan?.minScore ?? null);
  wallMinScore.current = wallScan?.minScore ?? null;
  useEffect(() => {
    if (drawnOnScan.current === wallScanId) return;
    drawnOnScan.current = wallScanId;
    setHolds([]);
    resetHoldEditor.current();
    setHoldFilter(
      wallMinScore.current == null ? NO_FILTER : { minScore: wallMinScore.current, minSize: null }
    );
  }, [wallScanId]);

  const lineEditor = usePhotoLine({
    naturalWidth: photo.photo?.naturalWidth ?? 1,
    naturalHeight: photo.photo?.naturalHeight ?? 1,
    line,
    onChange: setLine,
    allowCorridor: route.place !== 'crag',
  });
  const zoom = route.type === 'Route' ? lineEditor.zoom : holdEditor.zoom;
  const aspect = photo.photo ? photo.photo.naturalWidth / photo.photo.naturalHeight : null;
  // A phone held sideways lays the picture and the fields side by side — once
  // there is a picture. Until then the choice of one is a page of its own, laid
  // out as it always is. See ClimbEditorSplit.
  const split = useEditorSplit() && !!photo.photo;
  // The column's box for the bar of roles and the circuit's list, which the
  // surface keeps the state of and portals in.
  const [splitControls, setSplitControls] = useState<HTMLDivElement | null>(null);
  // A landscape photo, opened out to the height of a phone's screen — see
  // useExpandablePhoto. Laid out here, since the fields go into the same column.
  // Side by side it is open for good, and fills the column it is given.
  const expansion = useExpandablePhoto(aspect, zoom, split);

  // Pressing away from the picture puts the mark down: the setter has moved on
  // to the name or the grade, and the panel has nothing left to act on.
  const clearSelection =
    route.type === 'Route' ? lineEditor.clearSelection : holdEditor.clearSelection;
  useClearSelectionOutside(clearSelection);

  // What the climb already says about itself. Written into the fields once,
  // when it arrives — after that they are the setter's, and a second pass would
  // undo whatever they had started typing.
  const filledFields = useRef(false);
  useEffect(() => {
    const climb = source.climb;
    if (!climb || filledFields.current) return;
    filledFields.current = true;

    setName(climb.name ?? '');
    setGradeId(climb.grade_id ? String(climb.grade_id) : '');
    setHoldColor(climb.hold_color ?? '');
    if (climb.position != null) setPosition(String(climb.position));
    // Whether this climb is a circuit, and which hand its first move is made
    // with — read back out of the holds, where every save wrote it.
    setCircuitState(!!climb.circuit);
    setForcedHandsState(!!climb.forced_hands);
    setStartingHand(startingPhotoHand(climb.holds ?? []));
  }, [source.climb]);

  // The drawing, which cannot be placed until the photo has been decoded: a mark
  // is a fraction of the picture's own size, and the scan's detections are on a
  // grid of their own that only means something against it.
  //
  // What the climb already has wins. The detections are a guess — one mark per
  // hold the analysis found in this colour — and they are only worth drawing on a
  // climb that has nothing yet, which is exactly the draft a scan just made.
  const filledDrawing = useRef(false);
  useEffect(() => {
    if (filledDrawing.current || source.loading || !photo.photo) return;
    const climb = source.climb;
    if (!climb) return;
    filledDrawing.current = true;

    if (climb.line) setLine(climb.line);
    if (climb.holds?.length) return setHolds(climb.holds);

    const detections = source.scan?.detections ?? [];
    if (detections.length === 0) return;
    setHolds(
      holdsFromDetections(detections, photo.photo.naturalWidth, photo.photo.naturalHeight)
    );
  }, [source.loading, source.climb, source.scan, photo.photo]);

  // The walls this gym was last photographed on, for its staff. A gym's shortcut
  // to a picture it already has, where a crag's is its sectors below — see
  // ClimbPhotoSource for why the two places are offered different things.
  const recentPhotos = useRecentClimbPhotos({
    place: route.place,
    locationSlug: route.locationSlug,
    locationId: location?.id,
    type: route.type,
  });
  // And the walls it has had scanned, offered above those: picked, a scanned
  // wall comes with the holds the scan found on it. Only while a climb is being
  // put up — one made off a scan is made through the scan, and a climb being
  // corrected already exists.
  const { scans: recentScans, liveScan: liveScanOffered } = useRecentClimbScans({
    place: route.place,
    locationId: location?.id,
    type: route.type,
    creating: !route.editing,
  });
  // A boulder is chosen from a scan's holds; a route is one line drawn over the
  // wall, and to a route a scan is a photograph.
  const scansOfferHolds = route.type === 'Boulder';

  /**
   * **A photo taken now, read for holds while the setter waits** (#320):
   * whenever the hold model runs on Lambda, a boulder's camera or gallery picture
   * goes up at once as a scan, and its holds arrive on it as on any scanned
   * wall — the picture on screen all along, covered by how far the reading
   * has got (`useLiveScan`). A boulder being put up only: a route is a line,
   * and a climb being corrected already has its drawing.
   */
  const readsPhotoForHolds = liveScanOffered && scansOfferHolds && !route.editing && !source.climb;
  const attachScan = photo.attachScan;
  const liveScan = useLiveScan({
    locationSlug: location?.slug,
    upload,
    onDone: (outcome: LiveScanOutcome) => {
      if (outcome.kind === 'found') return attachScan(outcome.url, outcome.scan);
      // Either way the photo stays, to be circled by hand.
      toast({
        title: t(outcome.kind === 'empty' ? 'climbEditor.liveScan.empty' : 'climbEditor.liveScan.failed'),
        description: t('climbEditor.liveScan.byHand'),
      });
    },
  });
  const cancelLiveScan = liveScan.cancel;

  /** The camera's or the gallery's picture — read for holds, where it can be. */
  const chooseFile = async (file: File) => {
    cancelLiveScan();
    const url = await photo.choose(file);
    if (url && readsPhotoForHolds) void liveScan.start(file, url);
  };

  // The location's sectors that have a photo of their own. Outdoors that photo
  // *is* what a route is drawn on, so it is offered beside the camera.
  useEffect(() => {
    if (!route.locationSlug || route.place === 'board') return;
    let cancelled = false;
    zonesService
      .getZonesForLocation(route.locationSlug, route.place)
      .then((loaded) => {
        if (cancelled) return;
        setAllZones(loaded);
        setZones(
          loaded
            .filter((zone) => !!zone.image_url)
            .map((zone) => ({
              id: zone.id,
              name: zone.name,
              imageUrl: zone.image_url as string,
              thumbnailUrl: zone.image_small_url,
            }))
        );
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [route.locationSlug, route.place]);

  // A sector's photo is a way in on rock only. Outdoors the sector *is* the
  // picture a line is drawn on; indoors it is a wide shot of a corner of the
  // room that nobody would draw a problem on, and offering it was a list of
  // pictures with no use. The sectors are still loaded either way — the extras
  // dialog asks which one the climb is in.
  const offeredZones = route.place === 'crag' ? zones : [];

  // The sector travels with the picture, exactly as it does when a sector's own
  // photo is picked: the same wall is the same sector, so it is one answer the
  // form no longer has to ask for.
  const pickRecentPhoto = (recent: RecentClimbPhoto) => {
    if (!recent.url) return;
    cancelLiveScan();
    void photo.chooseFromUrl(recent.url, { zoneId: recent.zone_id });
  };

  const pickZonePhoto = (zone: ZonePhoto) => {
    cancelLiveScan();
    void photo.chooseFromUrl(zone.imageUrl, { zoneId: zone.id });
  };

  /**
   * A scanned wall, picked — with its sector, like any other wall's photo, and
   * with the holds the scan found where there are any for a boulder to take.
   *
   * The holds are read **before** the picture goes up, so the wall is never on
   * screen as one to draw circles on. A wall whose holds will not load is still
   * a photograph: that costs the setter the placeholders, not the picture.
   */
  const pickScan = async (offered: RecentClimbScan) => {
    if (!offered.url || !route.locationSlug) return;
    cancelLiveScan();

    const scan =
      scansOfferHolds && offered.holds_count > 0
        ? await climbScansService
            .getScan(route.locationSlug, offered.scan_id)
            .then(wallScanFrom)
            .catch(() => null)
        : null;

    void photo.chooseFromUrl(offered.url, { zoneId: offered.zone_id, scan });
  };

  /**
   * The confidence a reader let go of, saved on the scan where they may — the
   * gym's climb staff, as on the panel, whose slider it is — so the panel and
   * this editor hide the same holds. A climber's is this editor's alone.
   */
  const [savingConfidence, setSavingConfidence] = useState(false);
  const savesConfidence = !!wallScan && access.signedIn && !access.memberUserId;
  const commitConfidence = async (minScore: number | null) => {
    if (!wallScan || !location || !savesConfidence) return;
    setSavingConfidence(true);
    try {
      await climbScansService.updateScan(location.slug, wallScan.id, { min_score: minScore });
    } catch (error) {
      toast({
        title: t('climbEditor.holdFilter.saveFailed'),
        description: error instanceof Error ? error.message : t('errors.unexpectedError'),
        variant: 'destructive',
      });
    } finally {
      setSavingConfidence(false);
    }
  };
  const wallDetections = wallScan?.detections;
  const confidenceFloor = useMemo(
    () => (wallDetections ? minScoreFloor(wallDetections) : null),
    [wallDetections]
  );
  const holdSizes = useMemo(() => (wallDetections ? sizeRange(wallDetections) : null), [wallDetections]);

  const gradeOptions = useMemo<WheelPickerOption<string>[]>(
    () => gradesFor(location, route.type).map((grade) => ({ value: grade.id, label: grade.name })),
    [location, route.type]
  );

  // Only a boulder can be a circuit: it is circled hold by hold, so its circles
  // can carry an order. A route is one line from the ground to the anchor, which
  // is already in the order it is climbed.
  const offersCircuit = route.type === 'Boulder';

  /**
   * The drawing as it is read back and saved: the order the circles are in, with
   * the hands the circuit forces written onto them.
   *
   * Derived rather than kept in state, so the alternation cannot drift from the
   * order it follows from — the same reason the server works it out again on the
   * way in (see `Boulder#assign_photo_hands`). Off a circuit it is the drawing
   * with any hands stripped off, which is what the server stores there too.
   */
  const circuitHolds = useMemo(
    () =>
      circuit && forcedHands ? assignPhotoHands(holds, startingHand) : clearPhotoHands(holds),
    [circuit, forcedHands, holds, startingHand]
  );

  const setCircuit = (on: boolean) => {
    setCircuitState(on);
    // Circles drawn as a set have no order; becoming a circuit gives them one,
    // and a climb is climbed from its starts to its top.
    if (on) return setHolds(orderPhotoHolds);

    // Leaving circuit mode takes the hands with it: they alternate down a
    // sequence, and a set of circles has none. The server drops them the same
    // way, so a form that kept the switch on would promise what the save would
    // not keep.
    setForcedHandsState(false);
    setCircuitPanelOpen(false);
  };

  const setForcedHands = (on: boolean) => {
    setForcedHandsState(on);
    // Hands are read off the climbed order, so switching them on settles that
    // order first — the same thing circuit mode does when it is switched on over
    // circles painted as a set.
    if (on) setHolds(orderPhotoHolds);
  };

  const missing = useMemo(
    () => {
      const wanted = climbRequirements({
        route: { ...route, photoRequired },
        name,
        gradeId,
        holdColor,
        hasPhoto: !!photo.photo,
        holds,
        line,
      });

      // A contributor answers for the picture and for nothing else. The grade and
      // the hold type are the gym's own answers, given when the climb went up and
      // not shown here — left in, they would be a save button that never lights
      // and no control on the page to make it.
      return access.contributing
        ? wanted.filter((key) => requirementField(key) !== 'grade' && requirementField(key) !== 'holdColor')
        : wanted;
    },
    [route, photoRequired, access.contributing, name, gradeId, holdColor, photo.photo, holds, line]
  );

  /**
   * The same list, split by whether it stops the save.
   *
   * Everything drawn on the picture is advice — strongly meant, never in the way
   * (see `isDrawingAdvice`): the caption names it all along and the save asks
   * about it once. What is left is what the climb cannot be written without, and
   * that is what the button waits for.
   */
  const advice = useMemo(() => missing.filter(isDrawingAdvice), [missing]);
  const required = useMemo(() => missing.filter((key) => !isDrawingAdvice(key)), [missing]);

  // What is still wanted, sorted by where it is answered: the drawing says so on
  // the picture and the fields say so themselves.
  //
  // The fields only say it once the save has been pressed. A form that opens
  // red has told the setter off for not yet having filled in a form they have
  // just been handed; the picture's caption is the other way round, because
  // nothing else on the page could say that three of these forty holds have to
  // be told apart. Once shown, each mark goes as its own answer arrives.
  const [showFieldErrors, setShowFieldErrors] = useState(false);
  const missingFields = useMemo(
    () => new Set<ClimbRequirementField>(required.map(requirementField)),
    [required]
  );
  const missingOnPhoto = useMemo(
    () => advice.map((key) => t(shortRequirementKey(key))),
    [advice, t]
  );
  const invalid = (field: ClimbRequirementField) =>
    showFieldErrors && missingFields.has(field);

  /**
   * The save, pressed before its time: mark what is wanted and go to the first
   * of it. Nothing is said in a dialog — the answers are all on this page, and a
   * list that has to be dismissed before any of them can be given is a step
   * between the setter and the work.
   */
  const showWhatIsMissing = () => {
    setShowFieldErrors(true);
    scrollToFirstRequirement([...missingFields]);
  };

  useBreadcrumb(
    () => ({
      onBack: () => navigate(-1),
      hideHomeLogo: true,
      segments: [
        ...(location
          ? [
              {
                label: location.name,
                avatarUrl: location.logoUrl ?? undefined,
                onClick: () => navigate(`/${placePath(route.place)}/${location.slug}`),
              },
            ]
          : []),
        // Putting a new one up is one place: the trail ends here.
        ...(route.editing
          ? [
              // The climb being worked on, called what the wall calls it — a gym
              // climb often has no name at all, and "" is not a breadcrumb — and
              // a way back to it, since this page was very likely reached from
              // there and correcting it is not the same as reading it.
              {
                label: name || (source.climb ? climbTitle(source.climb) : t('climbCreate.title')),
                onClick: source.climb?.location
                  ? () => navigate(climbUrl(source.climb as Climb))
                  : undefined,
              },
              // And what is being done to it, which is the page itself.
              { label: t('common.edit') },
            ]
          : [{ label: t('climbCreate.title') }]),
      ],
      loading,
    }),
    [location?.name, location?.logoUrl, location?.slug, route.place, route.editing, name, source.climb, loading, navigate, t]
  );

  /**
   * Put the climb up.
   *
   * `rect` is how much of the photo to keep: the whole of it, or what was on
   * screen. Cropping happens here rather than as the picture is zoomed, because
   * until the save nobody has decided that the framing is the climb — and
   * everything drawn is a fraction of the picture, so cutting it moves every
   * mark (see crop.ts).
   */
  const save = async (rect: CropRect) => {
    if (!location) return;
    // A photo still being read is not waited for: the climb goes up as drawn.
    cancelLiveScan();
    setPendingCrop(null);
    setSaving(true);

    try {
      const cropping = !!photo.photo && isCrop(rect);
      // A scanned wall is on the server already, and the climb is given the
      // scan's own photograph there: only a cut of it has to go up, and the
      // whole of it is kept as the cut's original by that same call.
      let file = wallScan && !cropping ? null : (photo.photo?.file ?? null);
      // The whole shot, kept only when what is saved is a cut of it. Nothing
      // here shows it again: it is for the next climb on this wall, which wants
      // the wall rather than this one's framing (see useRecentClimbPhotos).
      let originalFile: File | null = null;
      let savedHolds = holds;
      let savedLine = line;

      if (photo.photo && cropping) {
        const cut = await cropImage(
          photo.photo.url,
          rect,
          photo.photo.naturalWidth,
          photo.photo.naturalHeight
        );
        savedHolds = cropHolds(holds, rect, cut.width, cut.height);
        savedLine = cropLine(
          line,
          rect,
          photo.photo.naturalWidth,
          photo.photo.naturalHeight,
          cut.width,
          cut.height
        );
        originalFile = wallScan ? null : file;
        file = cut.file;
        // What is saved is what the setter should go on seeing — except on a
        // scanned wall, which stays whole on screen: the next climb on it is
        // chosen off the same placeholders, and they were measured against all
        // of it.
        if (!wallScan) {
          photo.replaceWith(cut.file, cut.width, cut.height);
          setHolds(savedHolds);
          setLine(savedLine);
        }
      }

      // The hands go on last, over whatever survived the crop: a circle outside
      // the framing is gone from the climb, and the alternation follows the run
      // that is left rather than the one that was drawn.
      savedHolds =
        circuit && forcedHands
          ? assignPhotoHands(savedHolds, startingHand)
          : clearPhotoHands(savedHolds);

      const signedId = file ? await upload(file) : null;
      // Never at the cost of the save: the climb is its picture and its marks,
      // and losing all of that because a second copy of the photo would not go
      // up would be a poor trade for a shortcut the next climb might not need.
      const originalSignedId = originalFile
        ? await upload(originalFile).catch(() => null)
        : null;
      // The picture and what is drawn on it: the heart of a save, and the whole
      // of a contributed one.
      const drawing = {
        ...(signedId ? { image_signed_id: signedId } : {}),
        ...(originalSignedId ? { original_image_signed_id: originalSignedId } : {}),
        ...(savedHolds.length > 0 ? { holds: savedHolds } : {}),
        ...(savedLine ? { line: savedLine } : {}),
        // Whether the circles are a sequence, and whether that sequence names the
        // hand each hold is taken with. Sent whichever way they are set, so
        // turning either off is saved as well as turning it on — and only where
        // the climb could have one at all: the server refuses neither, but a route
        // has no order to claim.
        ...(offersCircuit ? { circuit, forced_hands: circuit && forcedHands } : {}),
      };

      // What describes the climb rather than its picture. None of it is sent by a
      // contributor: those are the place's own answers, this page did not show
      // them any of it, and the server would drop them anyway (see
      // #photo_contribution_attributes).
      const described = access.contributing
        ? {}
        : {
            grade_id: gradeId,
            ...(route.usesHoldColor && holdColor ? { hold_color: holdColor } : {}),
            ...(route.usesPosition && position ? { position: Number(position) } : {}),
            // Drawing on a sector's photo says which sector the climb is on, so
            // that answer is already given and is not asked for again.
            ...(photo.photo?.zoneId ? { zone_id: photo.photo.zoneId } : {}),
          };

      const common = {
        // Only where it was asked for before the save — where the climb is known
        // by its name, or where a contributor is giving one to a climb that has
        // none. Everywhere else the name is one of the things asked once the climb
        // is up (see ClimbExtrasDialog), and sending an empty one here would blank
        // the name of a climb being corrected. Which is also why a field that was
        // left empty is left out where a name is not required at all: nothing
        // typed is not an answer.
        ...(asksName && (route.nameRequired || name.trim()) ? { name: name.trim() } : {}),
        ...described,
        ...drawing,
      };

      // Four ways to put a climb up. Which of the first three this is was
      // decided before the page ever opened; the fourth, by the photo picked.
      //
      // A scan's draft exists already but is not a climb anybody can see yet, so
      // it is *published* — one call that gives it its picture, its marks and
      // its name at once, and moves the scan's colour on to done. The sector and
      // the setter travel with it because the draft was already told them: left
      // out, they would survive, but sending what we opened on keeps this call
      // the whole answer.
      //
      // A new climb chosen off a scanned wall is made **through the scan**: it
      // is one of that wall's climbs, so the scans panel lists it among them
      // rather than going on offering its holds as untouched. The scan's photo,
      // its sector and its next setter come with it, as they do on the panel.
      //
      // Not the setter for a climber with no role at the gym: the scan's
      // rotation is the gym's setters taking turns, and none of them set a
      // problem a climber picked off the wall. Naming nobody has the server
      // credit the climber (ClimbsController#default_route_setter_id).
      const member = !!access.memberUserId;
      const created = source.scan && source.climb
        ? await climbScansService.publishDraftClimb(source.climb.id, {
            ...common,
            zone_id: source.climb.zone?.id ?? null,
            route_setter_id: member
              ? source.climb.route_setter?.id ?? null
              : source.scan.routeSetter?.id ?? null,
          })
        : source.climb
          ? await climbsService.updateClimb(
              source.climb.id,
              route.type === 'Route' ? 'route' : 'boulder',
              common
            )
          : wallScan
            ? await climbScansService.publishClimbOnScan(location.slug, wallScan.id, {
                name: common.name ?? '',
                grade_id: gradeId,
                zone_id: photo.photo?.zoneId ?? null,
                route_setter_id: member ? null : wallScan.routeSetter?.id ?? null,
                color_name: holdColor || null,
                holds: savedHolds,
                circuit,
                forced_hands: circuit && forcedHands,
                // Only where the setter framed the climb: the cut, which the
                // endpoint knows to keep the scan's whole wall behind.
                image_signed_id: signedId ?? undefined,
                // The shapes taken off the wall, kept on the scan's row so it
                // can draw them back without measuring anything again.
                detected_holds: wallScan.detections.filter(
                  (_detection, index) =>
                    !!placeholders[index] && isPlaceholderTaken(placeholders[index], holds)
                ),
              })
            : await climbsService.createClimb({
                type: route.type === 'Route' ? 'route' : 'boulder',
                location_id: location.id,
                // Named here as well as in `described` above: a climb cannot be
                // created without a grade, and only a *correction* of one that
                // exists can leave the field out (which a contributor does).
                grade_id: gradeId,
                ...common,
              });

      // Put it in the round it was made for, before anything else can go wrong:
      // a climb created here and left off its round is exactly the half-finished
      // state the setter would then have to repair by hand.
      if (roundToJoin && !source.climb) {
        try {
          await competitionRoundsService.addClimbToRound(roundToJoin.slug, roundToJoin.id, {
            climb_id: created.id,
            requires_judge: roundToJoin.requiresJudge,
          });
        } catch (error) {
          // The climb exists either way, so say what happened rather than
          // pretending the save failed.
          toast({
            title: t('climbEditor.addToRoundFailed'),
            description: error instanceof Error ? error.message : t('errors.unexpectedError'),
            variant: 'destructive',
          });
        }
      }

      // A contributor is finished: they came to photograph a climb, the picture
      // is up, and everything the extras dialog asks for next belongs to the gym.
      // So they are thanked and sent to the climb itself — which is now worth
      // looking at, which is why they photographed it.
      if (access.contributing) {
        toast({ title: t('climbEditor.photoAdded') });
        return navigate(`/${placePath(route.place)}/${location.slug}/climbs/${created.slug}`);
      }

      setSaved(created);
    } catch (error) {
      toast({
        title: t('climbCreate.summaryStep.createError'),
        description: error instanceof Error ? error.message : t('errors.unexpectedError'),
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  /**
   * The save, asking first where the photo has been zoomed in on.
   *
   * A setter who zoomed to place a hold precisely did not mean to crop, and one
   * who framed the climb did — the same gesture, two intentions, so the only
   * honest thing is to ask which it was.
   */
  const requestSave = () => {
    const rect = visibleRect(zoom);
    // Only a zoom is a framing. A photo opened out shows a slice of the wall
    // before anybody has zoomed into it, and that slice is the screen's shape,
    // not a choice.
    if (photo.photo && !zoom.atRest && isCrop(rect)) return setPendingCrop(rect);
    void save(WHOLE_PHOTO);
  };

  /**
   * The save, pressed with everything it waits for answered — and perhaps with
   * nothing drawn on the picture.
   *
   * That is allowed, and it is put to the setter once before the climb goes up
   * bare: the caption has been saying what is missing all along, but a setter
   * who has been working on the fields may never have looked back up at it. See
   * `UnmarkedClimbDialog`.
   */
  const [confirmingUnmarked, setConfirmingUnmarked] = useState(false);
  const pressSave = () => {
    if (advice.length > 0) return setConfirmingUnmarked(true);
    requestSave();
  };

  if (loading || source.loading) {
    return <Skeleton className="mx-auto mt-8 h-96 w-full max-w-xl" />;
  }

  if (!location) {
    return (
      <div className="mx-auto mt-8 max-w-xl space-y-4 p-4">
        <Button variant="ghost" size="sm" onClick={() => navigate(-1)}>
          <ArrowLeft className="mr-2 size-4" />
          {t('common.back')}
        </Button>
        <p className="text-muted-foreground">{t('climbCreate.errors.gymNotFound')}</p>
      </div>
    );
  }

  // 1 and 2. The picture and what is being drawn on it. The controls are
  // not above it: they stand at the mark in hand, for as long as one is
  // in hand, and in the corner of the picture for what acts on the
  // drawing as a whole.
  const surface = photo.photo && (
    <PhotoClimbSurface
      photo={photo.photo}
      holdEditor={holdEditor}
      lineEditor={lineEditor}
      drawsLine={route.type === 'Route'}
      // On rock the line is the whole topo and there are no holds to tell
      // apart, so there is no band to set.
      allowCorridor={route.place !== 'crag'}
      // What the picture itself is still missing — a start, a top, a
      // line. There is no field for any of it, so it is said where it is
      // answered.
      missing={missingOnPhoto}
      onChangePhoto={() => setChangingPhoto(true)}
      scanned={!!wallScan}
      // Circuit mode, beside the button that changes the picture: the order
      // is painted on the photo, so what turns it on stands on the photo.
      circuit={
        offersCircuit
          ? {
              on: circuit,
              onToggle: setCircuit,
              panelOpen: circuitPanelOpen,
              onTogglePanel: setCircuitPanelOpen,
              forcedHands,
              onForcedHandsChange: setForcedHands,
              onStartingHandChange: setStartingHand,
              // The list reads the run back with its hands on, and writes
              // back the order alone — the hands follow from it.
              holds: circuitHolds,
              onHoldsChange: setHolds,
            }
          : undefined
      }
      expansion={expansion}
      // Side by side, the bar and the circuit's list go to the column.
      controlsTarget={split ? splitControls : null}
      liveScan={liveScan.state ? { state: liveScan.state, onSkip: cancelLiveScan } : null}
      holdFilter={
        wallScan && placeholders.length > 0
          ? {
              filter: holdFilter,
              onChange: setHoldFilter,
              onConfidenceCommit: (minScore) => void commitConfidence(minScore),
              confidenceFloor,
              sizeRange: holdSizes,
              hidden: placeholders.length - shownPlaceholders.length,
              total: placeholders.length,
              saving: savingConfidence,
            }
          : undefined
      }
    />
  );

  // 3. What identifies the climb — held back until there is a photo. The
  // grade and the holds both describe a climb that cannot be drawn yet,
  // and putting them up first asks the setter to fill in a form about
  // nothing.
  //
  // The name is usually not here: nobody calls a gym boulder by one, so
  // it is asked for once the climb is up, with the rest of what is
  // optional (see ClimbExtrasDialog). It stays on the page for the two
  // who have no such afterwards — a climb genuinely known by its name (a
  // competition problem), and a contributor, who gets no dialog of
  // extras. See `asksName`.
  const fields = (photo.photo || skippedPhoto) && (
    <div
      className={split ? 'space-y-4 px-3 pt-2' : 'space-y-4 px-4 pt-5'}
      data-testid="climb-info-row"
    >
      {asksName && (
        <div {...requirementAnchor('name')}>
          <ValidatedTitleInput
            title={name}
            locationId={location.id}
            type={route.type === 'Route' ? 'route' : 'boulder'}
            // A climb being corrected — or a scan's draft, which was saved
            // under this very name before the page opened — is not a climb
            // it can clash with. Without this the field reports the climb
            // against itself, and every edit opens on an error.
            excludeClimbId={source.climb?.id ?? null}
            onTitleChange={setName}
            hasError={invalid('name')}
            errorMessage={t('climbEditor.missing.field', { item: t('climbEditor.missing.name') })}
            compactSuggestions
          />
          {/* Where the name is not what the climb is known by, it is a gift
              rather than a field: the photographer is the first person to
              stand in front of this one since it went up. */}
          {!route.nameRequired && (
            <p className="pt-1 text-xs text-muted-foreground">{t('climbEditor.nameOptional')}</p>
          )}
        </div>
      )}

      {/* A contributor is here for the picture. What the climb *is* was
          settled when it went up — the grade, the hold type, the sector — so
          it is read back to them as the caption of what they are
          photographing, and not as fields they cannot save (see
          useClimbEditorAccess). */}
      {access.contributing ? (
        source.climb && describeClimb(source.climb) ? (
          <p className="border-t pt-4 text-sm text-muted-foreground" data-testid="climb-described">
            {describeClimb(source.climb)}
          </p>
        ) : null
      ) : (
      <>
      {/* Side by side the column is narrow, and the two stand one under the
          other rather than squeezing each other's labels onto three lines. */}
      <div
        className={`flex gap-4 border-t pt-4 text-sm ${
          split ? 'flex-col items-start' : 'items-center justify-between'
        }`}
      >
        <div {...requirementAnchor('grade')}>
          <WheelPickerField
            label={t('climbs.board.fields.grade')}
            placeholder={t('climbs.board.fields.gradePlaceholder')}
            value={gradeId || null}
            options={gradeOptions}
            onChange={setGradeId}
            invalid={invalid('grade')}
            testId="climb-grade-picker"
          />
        </div>

        {/* Indoors this is how one climb is told from the one crossing it.
            On rock there is nothing to tell apart, and the model refuses a
            colour there outright. */}
        {route.usesHoldColor && (
          <div {...requirementAnchor('holdColor')}>
            <HoldTypeDialog
              value={holdColor}
              onChange={setHoldColor}
              locationId={location.id}
              invalid={invalid('holdColor')}
            />
          </div>
        )}
      </div>

      {/* A crag orders its routes along the cliff instead of colouring their
          holds — and the model refuses a hold colour there outright. */}
      {route.usesPosition && (
        <div className="space-y-2 border-t pt-4">
          <Label htmlFor="climb-position">{t('climbCreate.position')}</Label>
          <Input
            id="climb-position"
            type="number"
            min={1}
            value={position}
            onChange={(event) => setPosition(event.target.value)}
            placeholder={t('climbCreate.positionPlaceholder')}
          />
        </div>
      )}
      </>
      )}
    </div>
  );

  return (
    <div className="mx-auto max-w-xl">
      {split ? (
        <ClimbEditorSplit
          aspect={aspect}
          title={location.name}
          picture={surface}
          controlsRef={setSplitControls}
        >
          {fields}
        </ClimbEditorSplit>
      ) : (
        <>
          {/* Out of main's gutter on every side that is between the picture and the
              screen: `-mx-4` sideways, and `-mt-3` for the row of padding above,
              which would otherwise sit as a band of page between the navbar and the
              photo. The picture is the page here, and it starts where the navbar
              ends — which is also what the height cap is measured from.

              Opened out, this is a column of the screen's height: the fields take
              what they need and the picture the rest (see editorExpandedHeight). */}
          <div
            className={`-mx-4 -mt-3 sm:-mx-6 ${expansion.expanded ? 'flex flex-col' : 'pb-28'}`}
            style={expansion.expanded ? { height: editorExpandedHeight() } : undefined}
          >
            {photo.photo ? (
              surface
            ) : (
              !skippedPhoto && (
                <ClimbPhotoSource
                  zones={offeredZones}
                  recent={recentPhotos}
                  scans={recentScans}
                  scansOfferHolds={scansOfferHolds}
                  onFile={(file) => void chooseFile(file)}
                  onZonePhoto={pickZonePhoto}
                  onRecentPhoto={pickRecentPhoto}
                  onScan={(scan) => void pickScan(scan)}
                  onSkip={photoRequired ? undefined : () => setSkippedPhoto(true)}
                  // Where the place would normally insist on a picture, going on
                  // without one is asked about first: it is not giving the photo up,
                  // it is handing it to whoever climbs the thing, and that is worth
                  // saying out loud to the one person who can still take it now.
                  skipWarning={
                    !photoRequired && route.photoRequired
                      ? t('climbEditor.photo.skipNote')
                      : undefined
                  }
                />
              )
            )}

            {fields}
          </div>
        </>
      )}

      {/* Nothing to save until there is a picture to save it against, and a
          button in the corner of an empty page is a question nobody asked. */}
      {(photo.photo || skippedPhoto) && (
        <ClimbEditorFab
          // The drawing is not counted here: it is urged rather than waited
          // for, and the press that finds it missing asks about it.
          ready={required.length === 0}
          saving={saving}
          onSave={pressSave}
          onIncomplete={showWhatIsMissing}
          label={t('climbEditor.save')}
        />
      )}

      {/* Everything answered but the picture left bare: allowed, and put to the
          setter once before the climb goes up that way. */}
      <UnmarkedClimbDialog
        open={confirmingUnmarked}
        missing={missingOnPhoto}
        onDismiss={() => setConfirmingUnmarked(false)}
        onConfirm={() => {
          setConfirmingUnmarked(false);
          requestSave();
        }}
      />

      {/* Zoomed in at the moment of saving: is the framing the climb, or was it
          just a closer look? Asked with both answers shown. */}
      <CropChoiceDialog
        photo={photo.photo}
        crop={pendingCrop}
        // With the hands on, so the two framings show the climb as it will be
        // seen — the hands themselves are put on again after the cut, see save.
        holds={circuitHolds}
        line={line}
        onChoose={(rect) => void save(rect)}
        onDismiss={() => setPendingCrop(null)}
      />

      <ClimbPhotoSource
        zones={offeredZones}
        recent={recentPhotos}
        scans={recentScans}
        scansOfferHolds={scansOfferHolds}
        open={changingPhoto}
        onOpenChange={setChangingPhoto}
        onFile={(file) => void chooseFile(file)}
        onZonePhoto={pickZonePhoto}
        onRecentPhoto={pickRecentPhoto}
        onScan={(scan) => void pickScan(scan)}
      />

      <NameSuggestionsDialog
        open={managingNames}
        onOpenChange={setManagingNames}
        text={nameSuggestions.text}
        onSave={nameSuggestions.save}
        count={nameSuggestions.names.length}
      />

      {/* Everything a save should never have waited for. */}
      {saved && (
        <ClimbExtrasDialog
          climb={saved}
          locationSlug={location.slug}
          zones={allZones}
          // Where the name was not asked for before the save, this is where it
          // is asked — with the gym's queue of names, which is the whole reason
          // a setter has one to hand.
          naming={
            route.nameRequired
              ? null
              : {
                  locationId: location.id,
                  type: route.type === 'Route' ? 'route' : 'boulder',
                  suggestions:
                    route.place === 'gym'
                      ? { names: nameSuggestions.names, onManage: () => setManagingNames(true) }
                      : undefined,
                  onUsed: nameSuggestions.consume,
                }
          }
          // A scan's draft was told its sector and its setter before this page
          // opened. Shown as the answers so far rather than as blanks, or saving
          // this dialog would quietly clear them.
          initialZone={saved.zone ?? source.climb?.zone ?? null}
          initialRouteSetter={saved.route_setter ?? source.scan?.routeSetter ?? null}
          memberUserId={access.memberUserId}
          // A scan is a wall of colours and each one is its own draft, so there
          // is no "another on this photo" to go to — there is the rest of the
          // scan.
          nextLabel={source.scan ? t('climbEditor.saved.backToScan') : undefined}
          onDone={(created) => {
            setSaved(null);
            // Back where they came from: a setter filling a round wants the
            // round, not the climb's own page.
            if (roundToJoin) {
              return navigate(`/competitions/${roundToJoin.slug}/rounds/${roundToJoin.id}`);
            }
            navigate(`/${placePath(route.place)}/${location.slug}/climbs/${created.slug}`);
          }}
          onNext={() => {
            setSaved(null);
            if (source.scan) {
              return navigate(`/gyms/${location.slug}/scans/${source.scan.scanId}`);
            }
            // Same wall, same photo, same sector, same setter — only the climb
            // changes. A setter putting up a circuit shoots the wall once and
            // draws six problems on it, so throwing the picture away would make
            // them find it again for every one of them. The drawing goes, since
            // that is what is different about the next one — and so does the
            // hold type, which is how the next problem is told apart from this
            // one on the same wall: kept, it would quietly put the two climbs
            // on the same holds.
            setName('');
            setGradeId('');
            setHoldColor('');
            setHolds([]);
            setLine(null);
            // Including whether the last one was a circuit: the next problem on
            // this wall is a page opening again, not the one just saved with its
            // holds taken out.
            setCircuitState(false);
            setForcedHandsState(false);
            setStartingHand('left');
            setCircuitPanelOpen(false);
            // The next climb is the page opening again, and a page that opens
            // red is telling the setter off for a form they have not been given
            // yet.
            setShowFieldErrors(false);
            // And the editors go back to how they opened: nothing in hand, the
            // start colour armed again, no undo reaching back into the climb
            // that was just saved. Without this the next problem would begin
            // painting in whatever colour the last one ended on.
            holdEditor.reset();
            lineEditor.clearSelection();
            window.scrollTo({ top: 0 });
          }}
        />
      )}
    </div>
  );
}

/** Where a place of this kind lives in the URL. */
function placePath(place: ClimbEditorRoute['place']): string {
  if (place === 'crag') return 'crags';
  if (place === 'competition') return 'competitions';
  return 'gyms';
}
